import { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  Briefcase,
  Calculator,
  Plus,
  Radar,
  Ship,
  Sparkles,
  Store,
} from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { CUADRO_DE_MODULO, type Modulo } from '@/components/layout/modulos';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { usePendientesDeDecidir } from '@/features/compite/hooks/use-pendientes-de-decidir';
import { useAlertasStore } from '@/features/dynamic-pricing/store/alertas-store';
import { destinoNuevaCotizacion } from '@/features/quotes/utils/destino-nueva-cotizacion';
import { cn } from '@/lib/utils/cn';
import { formatoPorcentaje, formatoPrecio } from '@/lib/utils/formato-precio';
import { BarraDeTramos, TarjetaDeModulo } from '../components/tarjeta-de-modulo';
import { NecesitaTuAtencion } from '../components/necesita-tu-atencion';
import { useResumenDeInicio } from '../hooks/use-resumen-de-inicio';
import {
  montoCorto,
  pendientesDelDia,
  posicionContraElMercado,
  variacion,
} from '../lib/resumen-de-inicio';
import { saludoDeLaHora } from '@/lib/utils/saludo-de-la-hora';

// El saludo, uno solo para todas las pantallas (recorrido 3-nov).
const saludo = saludoDeLaHora;

/** Las iniciales de los meses, para las barras de Cotiza («2026-05» → «M»). */
const INICIAL_DEL_MES = ['E', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

/**
 * Inicio (rediseño del 18-oct, artefacto «Inicio · Dashboard general»): cómo
 * están hoy Cotiza, Compite y Comercia, y lo que necesita una decisión.
 *
 * Reemplaza al Dashboard de cotizaciones, que no se pierde: todo lo que tenía
 * (indicadores, tendencia, pronóstico, clientes, márgenes, embudo…) vive ahora en
 * Cotiza › Visión general, como en el artefacto.
 *
 * Cada tarjeta aparece sólo si la persona tiene ese módulo (los mismos permisos
 * del menú), y sólo se piden sus datos: así nadie ve «Módulo no disponible» ni
 * se piden números que darían 403.
 */
export function InicioPage() {
  const { user, canSeeMenu, isModuleLocked } = useAuth();
  const { pathname } = useLocation();
  const abre = (claves: string[]) => claves.some((k) => canSeeMenu(k) && !isModuleLocked(k));

  const modulos = useMemo(
    () => ({
      cotiza: abre(['quotes', 'pricer-datec', 'approvals']),
      compite: abre(['pricing', 'data-import']),
      comercia: abre(['import-tracking']),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canSeeMenu, isModuleLocked]
  );
  const visibles = (['cotiza', 'compite', 'comercia'] as Modulo[]).filter((m) => modulos[m]);
  const r = useResumenDeInicio(modulos);

  // Compite: los precios que esperan decisión y los avisos sin ver.
  const preciosPorDecidir = usePendientesDeDecidir(
    modulos.compite && canSeeMenu('data-import'),
    pathname
  );
  const avisosSinVer = useAlertasStore((s) => s.noVistas);
  const cargarAvisos = useAlertasStore((s) => s.cargarResumen);
  // Los avisos son de Precios Dinámicos: el servidor (`/api/alertas`) pide ESE módulo, no el de
  // «Datos» que también abre Compite. Con sólo Datos, pedirlos daba 403 y ensuciaba la consola.
  const veAvisos = abre(['pricing']);
  useEffect(() => {
    if (veAvisos) void cargarAvisos();
  }, [veAvisos, cargarAvisos]);

  const nuevaCotizacion = modulos.cotiza ? destinoNuevaCotizacion(canSeeMenu) : null;

  // ── Cotiza ──
  const stats = r.cotiza.stats;
  const ingresos = stats?.comparison.current.revenue ?? 0;
  const delta = stats ? variacion(ingresos, stats.comparison.previous.revenue) : null;
  const meses = (stats?.quotesByMonth ?? []).slice(-7);
  const maxMes = Math.max(1, ...meses.map((m) => m.revenue));

  // ── Compite ──
  const posicion = r.compite.posicion;
  const enElMercado = posicion ? posicionContraElMercado(posicion.products) : null;
  const salud = r.compite.resumen?.salud;

  // ── Comercia ── (28-oct: la importación que se está armando, en tres pasos)
  const importacion = r.comercia.importacion;
  const tarifas = r.comercia.tarifas;
  const usdt = r.comercia.cambio?.summaries.find((s) => s.currentRate != null) ?? null;

  const pendientes = pendientesDelDia({
    aprobaciones: modulos.cotiza ? stats?.approvals.pending : undefined,
    cotizacionesPorVencer: modulos.cotiza ? stats?.expiringSoonQuotes.length : undefined,
    preciosPorDecidir: modulos.compite ? preciosPorDecidir : undefined,
    avisosSinVer: veAvisos ? (avisosSinVer ?? undefined) : undefined,
    lecturasFallidas: modulos.compite ? salud?.fallidas : undefined,
    tarifasVencidas: modulos.comercia ? tarifas?.expiredRates : undefined,
    tarifasPorVencer: modulos.comercia ? tarifas?.expiringSoon : undefined,
  });
  const cargandoPendientes =
    (modulos.cotiza && r.cotiza.cargando) ||
    (modulos.compite && r.compite.cargando) ||
    (modulos.comercia && r.comercia.cargando);

  const fecha = new Date().toLocaleDateString('es-BO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const nombre = user?.firstName ?? '';
  const nombresDeModulos = visibles.map((m) =>
    m === 'cotiza' ? 'Cotiza' : m === 'compite' ? 'Compite' : 'Comercia'
  );

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        {/* ── Saludo y atajos ── */}
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="space-y-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
              Resumen general · {fecha}
            </span>
            <h1 className="text-4xl font-semibold tracking-tight">
              {saludo()},{' '}
              <span className="bg-degradado-prixia-texto bg-clip-text text-transparent">
                {nombre}.
              </span>
            </h1>
            {nombresDeModulos.length > 0 && (
              <p className="text-base text-muted-foreground">
                Así están tus precios hoy en{' '}
                {nombresDeModulos.length === 1
                  ? nombresDeModulos[0]
                  : `${nombresDeModulos.slice(0, -1).join(', ')} y ${nombresDeModulos[nombresDeModulos.length - 1]}`}
                .
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {nuevaCotizacion && (
              <Atajo
                href={nuevaCotizacion.href}
                modulo="cotiza"
                icono={Plus}
                etiqueta={nuevaCotizacion.label}
                dataTour="dashboard-new-quote"
              />
            )}
            {modulos.compite && canSeeMenu('pricing') && (
              <Atajo
                href="/compite/competencia"
                modulo="compite"
                icono={Store}
                etiqueta="Agregar competidor"
              />
            )}
            {modulos.comercia && (
              <Atajo
                href="/comercia/productos"
                modulo="comercia"
                icono={Calculator}
                etiqueta="Calcular costo puesto"
              />
            )}
          </div>
        </div>

        {/* ── Una tarjeta por módulo ── */}
        {visibles.length > 0 && (
          <div
            data-tour="inicio-tarjetas"
            className={cn(
              'grid grid-cols-1 gap-5',
              visibles.length === 2 && 'lg:grid-cols-2',
              visibles.length >= 3 && 'lg:grid-cols-2 xl:grid-cols-3'
            )}
          >
            {modulos.cotiza && (
              <TarjetaDeModulo
                modulo="cotiza"
                icono={Briefcase}
                subtitulo="Cotizaciones y rentabilidad"
                href="/cotiza/vision-general"
                cargando={r.cotiza.cargando}
                etiquetaDelNumero="Ingresos aprobados este mes"
                numero={stats ? montoCorto(ingresos) : '—'}
                junto={<Variacion delta={delta} />}
                pie={[
                  { etiqueta: 'Cotizaciones', valor: stats?.comparison.current.total ?? '—' },
                  {
                    etiqueta: 'Aprobación',
                    valor:
                      stats && stats.quotes.total > 0
                        ? formatoPorcentaje((stats.quotes.approved / stats.quotes.total) * 100, {
                            decimales: 0,
                          })
                        : '—',
                  },
                  { etiqueta: 'Pendientes', valor: stats?.approvals.pending ?? '—' },
                ]}
              >
                {meses.length > 0 && (
                  <div aria-label="Ingresos aprobados por mes" role="img" className="pt-1">
                    <div className="flex h-16 items-end gap-2">
                      {meses.map((m, i) => (
                        <span
                          key={m.month}
                          className={cn(
                            'flex-1 rounded-md',
                            i === meses.length - 1 ? 'bg-primary' : 'bg-primary/25'
                          )}
                          style={{ height: `${Math.max(6, (m.revenue / maxMes) * 100)}%` }}
                          title={`${m.month}: ${montoCorto(m.revenue)}`}
                        />
                      ))}
                    </div>
                    <div className="mt-1.5 flex gap-2 text-center text-2xs text-muted-foreground">
                      {meses.map((m) => (
                        <span key={m.month} className="flex-1">
                          {INICIAL_DEL_MES[Number(m.month.slice(5, 7)) - 1] ?? ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </TarjetaDeModulo>
            )}

            {modulos.compite && (
              <TarjetaDeModulo
                modulo="compite"
                icono={Radar}
                subtitulo="Precios de la competencia"
                href="/compite/decisiones"
                cargando={r.compite.cargando}
                etiquetaDelNumero="Productos con precio de la competencia"
                numero={enElMercado ? enElMercado.conCompetencia.toLocaleString('es-BO') : '—'}
                junto={
                  posicion ? (
                    <span className="text-sm text-muted-foreground">
                      vs {posicion.summary.activeStoresCount}{' '}
                      {posicion.summary.activeStoresCount === 1 ? 'tienda' : 'tiendas'}
                    </span>
                  ) : null
                }
                pie={[
                  { etiqueta: 'Cambios 24 h', valor: salud?.cambios ?? '—' },
                  { etiqueta: 'Avisos sin ver', valor: avisosSinVer ?? '—' },
                  { etiqueta: 'Lecturas fallidas', valor: salud?.fallidas ?? '—' },
                ]}
              >
                {enElMercado && enElMercado.conCompetencia > 0 && (
                  <BarraDeTramos
                    etiqueta={`${enElMercado.masCaros} más caros, ${enElMercado.enLinea} en línea, ${enElMercado.masBaratos} más baratos que el más barato`}
                    tramos={[
                      {
                        clave: 'caros',
                        valor: enElMercado.masCaros,
                        clase: 'bg-alerta',
                        leyenda: `${enElMercado.masCaros} más caros`,
                      },
                      {
                        clave: 'linea',
                        valor: enElMercado.enLinea,
                        clase: 'bg-muted-foreground/30',
                        leyenda: `${enElMercado.enLinea} en línea`,
                      },
                      {
                        clave: 'baratos',
                        valor: enElMercado.masBaratos,
                        clase: 'bg-ok',
                        leyenda: `${enElMercado.masBaratos} más baratos`,
                      },
                    ]}
                  />
                )}
              </TarjetaDeModulo>
            )}

            {modulos.comercia && (
              <TarjetaDeModulo
                modulo="comercia"
                icono={Ship}
                subtitulo="Costo real de importación"
                href="/comercia/productos"
                cargando={r.comercia.cargando}
                etiquetaDelNumero={
                  importacion?.resumen.nacionalizado != null
                    ? `Costo nacionalizado de ${importacion.numero}`
                    : importacion
                      ? `Costo acumulado de ${importacion.numero}`
                      : 'Todavía no armaste una importación'
                }
                numero={
                  importacion
                    ? montoCorto(importacion.resumen.nacionalizado ?? importacion.resumen.acumulado)
                    : '—'
                }
                junto={
                  importacion && importacion.resumen.fob > 0 ? (
                    <span className="text-sm text-muted-foreground">
                      {formatoPorcentaje(
                        (((importacion.resumen.nacionalizado ?? importacion.resumen.acumulado) -
                          importacion.resumen.fob) /
                          importacion.resumen.fob) *
                          100,
                        { signo: true, decimales: 0 }
                      )}{' '}
                      sobre el FOB
                    </span>
                  ) : null
                }
                pie={[
                  {
                    etiqueta: 'Productos',
                    valor: importacion ? importacion.resumen.productos : '—',
                  },
                  {
                    etiqueta: usdt ? `${usdt.coin}/BOB` : 'Tipo de cambio',
                    valor: usdt?.currentRate != null ? formatoPrecio(usdt.currentRate) : '—',
                  },
                  { etiqueta: 'Tarifas por vencer', valor: tarifas?.expiringSoon ?? '—' },
                ]}
              >
                {importacion && (
                  <BarraDeTramos
                    etiqueta={`Producto ${importacion.productos.length > 0 ? 'listo' : 'pendiente'}, logística ${importacion.ruta && !importacion.ruta.desactualizada ? 'lista' : 'pendiente'}, costo nacionalizado ${importacion.resumen.nacionalizado != null ? 'listo' : 'pendiente'}`}
                    tramos={[
                      {
                        clave: 'producto',
                        valor: 1,
                        clase:
                          importacion.productos.length > 0 ? 'bg-ok' : 'bg-muted-foreground/30',
                        leyenda: 'Producto',
                      },
                      {
                        clave: 'logistica',
                        valor: 1,
                        clase:
                          importacion.ruta && !importacion.ruta.desactualizada
                            ? 'bg-ok'
                            : 'bg-muted-foreground/30',
                        leyenda: 'Logística',
                      },
                      {
                        clave: 'costo',
                        valor: 1,
                        clase:
                          importacion.resumen.nacionalizado != null
                            ? 'bg-ok'
                            : 'bg-muted-foreground/30',
                        leyenda: 'Costo nacionalizado',
                      },
                    ]}
                  />
                )}
              </TarjetaDeModulo>
            )}
          </div>
        )}

        {/* ── Lo que necesita una decisión ── */}
        {visibles.length > 0 && (
          <div data-tour="inicio-atencion">
            <NecesitaTuAtencion
              pendientes={pendientes}
              modulos={visibles}
              cargando={!!cargandoPendientes}
            />
          </div>
        )}
      </div>
    </AppLayout>
  );
}

/** Un atajo de arriba: una píldora con el punto del módulo (artefacto). */
function Atajo({
  href,
  modulo,
  icono: Icono,
  etiqueta,
  dataTour,
}: {
  href: string;
  modulo: Modulo;
  icono: typeof Plus;
  etiqueta: string;
  dataTour?: string;
}) {
  return (
    <Link
      to={href}
      data-tour={dataTour}
      className="inline-flex h-11 items-center gap-2.5 rounded-full border bg-card py-1 pl-1.5 pr-4 text-sm font-semibold shadow-card transition-colors hover:bg-muted"
    >
      <span
        className={cn('grid h-8 w-8 place-items-center rounded-full', CUADRO_DE_MODULO[modulo])}
      >
        <Icono className="h-4 w-4" aria-hidden="true" />
      </span>
      {etiqueta}
    </Link>
  );
}

/** «↓ 96 %» en rojo o «↑ 50 %» en verde, al lado del número de Cotiza. */
function Variacion({ delta }: { delta: number | null }) {
  if (delta == null) return null;
  const sube = delta >= 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
        sube ? 'bg-ok-suave text-ok' : 'bg-destructive/10 text-destructive'
      )}
      title="Contra el mes pasado"
    >
      {sube ? (
        <ArrowUp className="h-3 w-3" aria-hidden="true" />
      ) : (
        <ArrowDown className="h-3 w-3" aria-hidden="true" />
      )}
      {formatoPorcentaje(Math.abs(delta), { decimales: 0 })}
      <span className="sr-only"> contra el mes pasado</span>
    </span>
  );
}

export default InicioPage;
